import React, { useEffect, useRef } from 'react';
import { Card, CardActionArea, CardContent, Typography, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import useAntiScraping from '../hooks/useAntiScraping';
import { useLanguage } from '../contexts/LanguageContext';

const ProfessorListItem = ({ lecturer, departmentName }) => {
  const { isBot } = useAntiScraping();
  const { t, isArabic } = useLanguage();
  const nameRef = useRef(null);

  const lecturerName = lecturer?.full_name ?? lecturer?.name ?? 'Lecturer Name Missing';

  // Dynamic name rendering to prevent easy scraping
  useEffect(() => {
    let isMounted = true;
    
    if (nameRef.current && !isBot) {
      const timeoutId = setTimeout(() => {
        if (isMounted && nameRef.current) {
          nameRef.current.textContent = lecturerName;
        }
      }, 100);
      
      return () => {
        isMounted = false;
        clearTimeout(timeoutId);
      };
    } else if (nameRef.current && isBot) {
      nameRef.current.textContent = '***';
    }
    
    return () => {
      isMounted = false;
    };
  }, [lecturerName, isBot]);

  if (!lecturer) {
    return null;
  }

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        // Theme defaults handle radius and basic shadow
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 6,
          transform: 'translateY(-4px)',
        },
      }}
    >
      <CardActionArea
        component={Link}
        to={`/lecturer/${lecturer.id}`}
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start',
          justifyContent: 'flex-start'
        }}
      >
        <CardContent sx={{ p: 3, width: '100%' }}>
          {/* Lecturer Avatar & Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: 'primary.50',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: '1px solid',
                borderColor: 'primary.100'
              }}
            >
              <PersonIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                ref={nameRef}
                variant="h6"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  mb: 0.5,
                  userSelect: 'none',
                }}
              >
                Loading...
              </Typography>
              {departmentName && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ 
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 1,
                  }}
                >
                  {departmentName}
                </Typography>
              )}
            </Box>
          </Box>

          {/* View Profile Link */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              mt: 'auto',
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              width: '100%'
            }}
          >
            <Typography 
              variant="body2" 
              color="primary.main" 
              sx={{ 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              {t('View reviews', 'عرض التقييمات')}
              <ArrowForwardIcon sx={{ fontSize: 16, transform: isArabic ? 'rotate(180deg)' : 'none' }} />
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default ProfessorListItem;